import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    CheckCircle as CheckCircleIcon,
    ContentCopy as ContentCopyIcon,
    Download as DownloadIcon,
    MailOutline as MailIcon,
    PhonelinkLock as AppIcon,
    Refresh as RefreshIcon,
    WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import OtpInput from "react-otp-input";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";

import {
    useConfirmarDosFactorApp,
    useConfirmarDosFactorEmail,
    useDesactivarDosFactor,
    useDosFactorEstado,
    useRegenerarBackupCodes,
    useSetupDosFactorApp,
    useSetupDosFactorEmail,
} from "@/shared/actions/auth";
import { gridSizeMdLg12 } from "@/shared";
import { useMfaStatusStore } from "@/store/auth/mfa-status.store";

type Paso = "idle" | "elegir" | "setup_app" | "setup_email" | "mostrar_backup";

export default function TabDobleFactor() {
    const { data: estado, isLoading, refetch } = useDosFactorEstado();

    const setupApp = useSetupDosFactorApp();
    const confirmarApp = useConfirmarDosFactorApp();
    const setupEmail = useSetupDosFactorEmail();
    const confirmarEmail = useConfirmarDosFactorEmail();
    const desactivar = useDesactivarDosFactor();
    const regenerar = useRegenerarBackupCodes();

    const [paso, setPaso] = useState<Paso>("idle");
    const [otpauthUri, setOtpauthUri] = useState<string>("");
    const [secretoManual, setSecretoManual] = useState<string>("");
    const [emailMascarado, setEmailMascarado] = useState<string>("");
    const [codigo, setCodigo] = useState("");
    const [backupCodes, setBackupCodes] = useState<string[]>([]);

    const [passwordDesactivar, setPasswordDesactivar] = useState("");
    const [codigoDesactivar, setCodigoDesactivar] = useState("");
    const [usaBackupDesactivar, setUsaBackupDesactivar] = useState(false);
    const [confirmDesactivar, setConfirmDesactivar] = useState(false);
    const [confirmRegenerar, setConfirmRegenerar] = useState(false);
    // Confirmacion explicita: el usuario marca que guardo los codigos de
    // respaldo. Hasta que no lo marque, no puede cerrar el dialog. Esto
    // evita perder los codigos cerrando accidentalmente.
    const [backupGuardado, setBackupGuardado] = useState(false);

    const habilitado = !!estado?.habilitado;
    const esUsuarioIntegracion = !!estado?.es_usuario_integracion;
    const metodoLabel = useMemo(() => {
        if (!estado?.metodo) return "";
        return estado.metodo === "app" ? "App Autenticadora" : "Email";
    }, [estado?.metodo]);

    useEffect(() => {
        if (paso !== "setup_app" && paso !== "setup_email") setCodigo("");
    }, [paso]);

    // --- Handlers ---

    const iniciarSetupApp = async () => {
        try {
            const res = await setupApp.mutateAsync();
            setOtpauthUri(res.otpauth_uri);
            setSecretoManual(res.secreto);
            setPaso("setup_app");
        } catch (err: any) {
            const apiMsg = err?.response?.data?.message;
            toast.error(apiMsg || "No se pudo iniciar el setup con app autenticadora.");
        }
    };

    const iniciarSetupEmail = async () => {
        try {
            const res = await setupEmail.mutateAsync();
            setEmailMascarado(res.email_mascarado);
            setPaso("setup_email");
        } catch (err: any) {
            // Casos comunes: SMTP caido (503), usuario sin email (400),
            // ya tiene 2FA activo (400). El backend ya retorna el message
            // descriptivo (despues del fix de _enviar_correo_codigo).
            const apiMsg = err?.response?.data?.message;
            toast.error(
                apiMsg ||
                "No se pudo enviar el código por correo. Revisa con el admin la configuración SMTP.",
            );
        }
    };

    const confirmar = async () => {
        if (codigo.length !== 6) {
            toast.error("Ingresa los 6 dígitos del código");
            return;
        }
        try {
            const res =
                paso === "setup_app"
                    ? await confirmarApp.mutateAsync(codigo)
                    : await confirmarEmail.mutateAsync(codigo);
            setBackupCodes(res.backup_codes || []);
            setBackupGuardado(false);
            setPaso("mostrar_backup");
            toast.success("Doble factor activado");
            // Si venían del flujo de obligatoriedad, ya no hay nada pendiente.
            useMfaStatusStore.getState().clear();
            refetch();
        } catch (err: any) {
            const apiMsg = err?.response?.data?.message;
            toast.error(
                apiMsg ||
                (paso === "setup_email"
                    ? "Código incorrecto o expirado. Pide uno nuevo con 'Reenviar código'."
                    : "Código incorrecto. Verifica los 6 dígitos en tu app autenticadora."),
            );
            setCodigo("");
        }
    };

    const handleDesactivar = async () => {
        if (!passwordDesactivar) {
            toast.error("Ingresa tu contraseña para desactivar");
            return;
        }
        if (habilitado && !codigoDesactivar) {
            toast.error("Ingresa un código vigente o un código de respaldo");
            return;
        }
        try {
            await desactivar.mutateAsync({
                password: passwordDesactivar,
                codigo: codigoDesactivar || undefined,
                usa_backup: usaBackupDesactivar || undefined,
            });
            toast.success("Doble factor desactivado");
            setPasswordDesactivar("");
            setCodigoDesactivar("");
            setUsaBackupDesactivar(false);
            setConfirmDesactivar(false);
            refetch();
        } catch (err: any) {
            const apiMsg = err?.response?.data?.message;
            toast.error(apiMsg || "No se pudo desactivar. Revisa contraseña y código.");
        }
    };

    const handleRegenerar = async () => {
        if (!passwordDesactivar) {
            toast.error("Ingresa tu contraseña para regenerar");
            return;
        }
        try {
            const res = await regenerar.mutateAsync(passwordDesactivar);
            setBackupCodes(res.backup_codes);
            setBackupGuardado(false);
            setPasswordDesactivar("");
            setConfirmRegenerar(false);
            setPaso("mostrar_backup");
        } catch (err: any) {
            const apiMsg = err?.response?.data?.message;
            toast.error(apiMsg || "Contraseña incorrecta o error al regenerar.");
        }
    };

    const copiarBackup = () => {
        navigator.clipboard.writeText(backupCodes.join("\n"));
        toast.success("Códigos copiados al portapapeles");
    };

    const descargarBackup = () => {
        // Descargar codigos como TXT para que el usuario tenga respaldo
        // fuera del navegador (recomendado guardarlo en un gestor de contrasenas).
        const fechaIso = new Date().toISOString().slice(0, 10);
        const contenido =
            `Fibramax EVA - Códigos de respaldo MFA\n` +
            `Fecha: ${fechaIso}\n` +
            `IMPORTANTE: Cada código sirve UNA sola vez. Guárdalos en un lugar seguro.\n\n` +
            backupCodes.map((c, i) => `${i + 1}. ${c}`).join("\n") +
            `\n`;
        const blob = new Blob([contenido], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mfa-backup-codes-${fechaIso}.txt`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Códigos descargados");
    };

    const cerrarFlujo = () => {
        // En el paso mostrar_backup, forzar que el usuario marque "guardé"
        // antes de cerrar. Evita perder los códigos por cerrar accidental.
        if (paso === "mostrar_backup" && !backupGuardado) {
            toast.warning(
                "Marca 'Confirmo que guardé los códigos' antes de cerrar este diálogo.",
            );
            return;
        }
        setPaso("idle");
        setOtpauthUri("");
        setSecretoManual("");
        setEmailMascarado("");
        setBackupCodes([]);
        setCodigo("");
        setBackupGuardado(false);
    };

    if (isLoading) {
        return (
            <Grid container item {...gridSizeMdLg12} justifyContent="center" py={6}>
                <CircularProgress />
            </Grid>
        );
    }

    if (esUsuarioIntegracion) {
        return (
            <Grid container item {...gridSizeMdLg12} pb={4}>
                <Grid item xs={12}>
                    <Divider />
                    <Stack spacing={2} py={3}>
                        <Typography variant="h6">Doble Factor de Autenticación</Typography>
                        <Alert severity="info" variant="outlined">
                            Este usuario está marcado como <strong>usuario de integración (API)</strong>.
                            El doble factor no aplica a integraciones y no puede configurarse desde aquí.
                        </Alert>
                    </Stack>
                    <Divider />
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container item {...gridSizeMdLg12} pb={4}>
            <Grid item xs={12}>
                <Divider />
                <Stack spacing={3} py={3}>
                    <Stack direction={{ xs: "column", md: "row" }} alignItems="flex-start" spacing={2} justifyContent="space-between">
                        <Box>
                            <Typography variant="h6">Doble Factor de Autenticación</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
                                Añade una capa extra de seguridad al inicio de sesión solicitando un
                                código único cada vez que accedas.
                            </Typography>
                        </Box>
                        <Chip
                            label={habilitado ? `Activo — ${metodoLabel}` : "Inactivo"}
                            color={habilitado ? "success" : "default"}
                            icon={habilitado ? <CheckCircleIcon /> : undefined}
                            variant={habilitado ? "filled" : "outlined"}
                        />
                    </Stack>

                    {!habilitado && (
                        <Alert severity="info" variant="outlined">
                            Te recomendamos activar el doble factor usando una app autenticadora
                            (Google, Microsoft o Duo). No afecta a integraciones por API.
                        </Alert>
                    )}

                    {!habilitado ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <MetodoCard
                                    title="App Autenticadora"
                                    desc="Escanea un código QR con Google/Microsoft Authenticator, Duo o similares. Genera códigos sin conexión."
                                    icon={<AppIcon fontSize="large" color="primary" />}
                                    btn="Configurar con App"
                                    loading={setupApp.isPending}
                                    onClick={iniciarSetupApp}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <MetodoCard
                                    title="Correo electrónico"
                                    desc={`Recibirás un código de 6 dígitos en tu correo${estado?.email ? ` (${estado.email})` : ""} cada vez que inicies sesión.`}
                                    icon={<MailIcon fontSize="large" color="primary" />}
                                    btn="Configurar por Email"
                                    loading={setupEmail.isPending}
                                    onClick={iniciarSetupEmail}
                                    disabled={!estado?.email}
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <Stack spacing={2}>
                            <Alert severity="success" variant="outlined">
                                El doble factor está activo con el método:{" "}
                                <strong>{metodoLabel}</strong>.
                            </Alert>
                            <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={1}>
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    onClick={() => setConfirmRegenerar(true)}
                                    startIcon={<RefreshIcon />}
                                >
                                    Regenerar códigos de respaldo
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => setConfirmDesactivar(true)}
                                >
                                    Desactivar doble factor
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
                <Divider />
            </Grid>

            {/* Diálogo: Setup App */}
            <Dialog open={paso === "setup_app"} onClose={cerrarFlujo} maxWidth="xs" fullWidth>
                <DialogTitle>Configurar App Autenticadora</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} alignItems="center">
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            Escanea este código QR con tu aplicación autenticadora.
                        </Typography>
                        {otpauthUri && (
                            <Box sx={{ p: 2, bgcolor: "#fff", borderRadius: 1 }}>
                                <QRCodeSVG value={otpauthUri} size={200} />
                            </Box>
                        )}
                        {secretoManual && (
                            <Box width="100%">
                                <Typography variant="caption" color="text.secondary">
                                    ¿No puedes escanear? Ingresa manualmente este código:
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: "monospace",
                                            wordBreak: "break-all",
                                            bgcolor: "action.hover",
                                            p: 1,
                                            borderRadius: 1,
                                            flex: 1,
                                        }}
                                    >
                                        {secretoManual}
                                    </Typography>
                                    <Tooltip title="Copiar">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                navigator.clipboard.writeText(secretoManual);
                                                toast.success("Copiado");
                                            }}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Box>
                        )}
                        <Divider flexItem />
                        <Typography variant="body2">
                            Ingresa el código de 6 dígitos que muestra la app:
                        </Typography>
                        <OtpInputAdapter value={codigo} onChange={setCodigo} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cerrarFlujo}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={confirmar}
                        disabled={confirmarApp.isPending || codigo.length !== 6}
                    >
                        Verificar y activar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo: Setup Email */}
            <Dialog open={paso === "setup_email"} onClose={cerrarFlujo} maxWidth="xs" fullWidth>
                <DialogTitle>Configurar por Correo Electrónico</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Typography variant="body2">
                            Enviamos un código de 6 dígitos a <strong>{emailMascarado}</strong>.
                            Ingrésalo a continuación para activar el doble factor.
                        </Typography>
                        <Box display="flex" justifyContent="center">
                            <OtpInputAdapter value={codigo} onChange={setCodigo} />
                        </Box>
                        <Button
                            variant="text"
                            size="small"
                            onClick={iniciarSetupEmail}
                            disabled={setupEmail.isPending}
                        >
                            Reenviar código
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cerrarFlujo}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={confirmar}
                        disabled={confirmarEmail.isPending || codigo.length !== 6}
                    >
                        Verificar y activar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo: Backup codes — prominente y con confirmacion explicita */}
            <Dialog
                open={paso === "mostrar_backup"}
                onClose={cerrarFlujo}
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ bgcolor: "warning.main", color: "warning.contrastText" }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <WarningAmberIcon />
                        <span>Guarda tus códigos de respaldo</span>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
                        <AlertTitle sx={{ fontWeight: 700 }}>
                            ESTOS CÓDIGOS SE MUESTRAN UNA SOLA VEZ
                        </AlertTitle>
                        Si pierdes el acceso a tu app autenticadora o correo,
                        estos códigos son la <strong>única forma</strong> de
                        recuperar tu cuenta. <strong>Cópialos o descárgalos</strong>
                        {" "}y guárdalos en un lugar seguro (gestor de contraseñas,
                        impresión, etc). Cada código sirve <strong>una sola vez</strong>.
                    </Alert>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: 1.5,
                            fontFamily: "monospace",
                            bgcolor: "grey.100",
                            p: 2,
                            borderRadius: 1,
                            border: "2px dashed",
                            borderColor: "warning.main",
                        }}
                    >
                        {backupCodes.map((c) => (
                            <Typography
                                key={c}
                                variant="h6"
                                sx={{
                                    fontFamily: "monospace",
                                    letterSpacing: 1,
                                    textAlign: "center",
                                    userSelect: "all",
                                }}
                            >
                                {c}
                            </Typography>
                        ))}
                    </Box>

                    <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" rowGap={1}>
                        <Button
                            onClick={copiarBackup}
                            startIcon={<ContentCopyIcon />}
                            variant="outlined"
                            size="small"
                        >
                            Copiar al portapapeles
                        </Button>
                        <Button
                            onClick={descargarBackup}
                            startIcon={<DownloadIcon />}
                            variant="outlined"
                            size="small"
                        >
                            Descargar como TXT
                        </Button>
                    </Stack>

                    <FormControlLabel
                        sx={{ mt: 2 }}
                        control={
                            <Checkbox
                                checked={backupGuardado}
                                onChange={(e) => setBackupGuardado(e.target.checked)}
                            />
                        }
                        label={
                            <Typography variant="body2" fontWeight={600}>
                                Confirmo que guardé estos códigos en un lugar seguro
                                y entiendo que <strong>no se volverán a mostrar</strong>.
                            </Typography>
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={cerrarFlujo}
                        disabled={!backupGuardado}
                    >
                        Continuar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmar desactivar */}
            <Dialog open={confirmDesactivar} onClose={() => setConfirmDesactivar(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Desactivar doble factor</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Por seguridad, además de tu contraseña debes ingresar un
                        código vigente {estado?.metodo === "app" ? "de tu app autenticadora" : "que enviamos a tu correo"} o un código de respaldo.
                    </Typography>
                    <Stack spacing={1.5}>
                        <input
                            type="password"
                            autoComplete="current-password"
                            placeholder="Contraseña actual"
                            value={passwordDesactivar}
                            onChange={(e) => setPasswordDesactivar(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "10px 12px",
                                borderRadius: 6,
                                border: "1px solid #ccc",
                                fontSize: 14,
                            }}
                        />
                        <input
                            type="text"
                            inputMode={usaBackupDesactivar ? "text" : "numeric"}
                            placeholder={usaBackupDesactivar ? "Código de respaldo (XXXXX-XXXXX)" : "Código de 6 dígitos"}
                            value={codigoDesactivar}
                            onChange={(e) => setCodigoDesactivar(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "10px 12px",
                                borderRadius: 6,
                                border: "1px solid #ccc",
                                fontSize: 14,
                                letterSpacing: usaBackupDesactivar ? 1 : 4,
                                fontFamily: "monospace",
                            }}
                        />
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => setUsaBackupDesactivar(!usaBackupDesactivar)}
                        >
                            {usaBackupDesactivar
                                ? "Usar código TOTP/Email"
                                : "Usar código de respaldo"}
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDesactivar(false)}>Cancelar</Button>
                    <Button color="error" variant="contained" onClick={handleDesactivar} disabled={desactivar.isPending}>
                        Desactivar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmar regenerar */}
            <Dialog open={confirmRegenerar} onClose={() => setConfirmRegenerar(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Regenerar códigos de respaldo</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Se invalidarán los códigos anteriores. Ingresa tu contraseña.
                    </Typography>
                    <input
                        type="password"
                        autoComplete="current-password"
                        value={passwordDesactivar}
                        onChange={(e) => setPasswordDesactivar(e.target.value)}
                        style={{
                            width: "100%",
                            padding: "10px 12px",
                            borderRadius: 6,
                            border: "1px solid #ccc",
                            fontSize: 14,
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmRegenerar(false)}>Cancelar</Button>
                    <Button variant="contained" color="warning" onClick={handleRegenerar} disabled={regenerar.isPending}>
                        Regenerar
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}

// ------------------------------------------------------------------
// Helpers visuales
// ------------------------------------------------------------------

function MetodoCard({
    title,
    desc,
    icon,
    btn,
    onClick,
    loading,
    disabled,
}: {
    title: string;
    desc: string;
    icon: React.ReactNode;
    btn: string;
    onClick: () => void;
    loading?: boolean;
    disabled?: boolean;
}) {
    return (
        <Box
            sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 1,
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center">
                {icon}
                <Typography variant="subtitle1" fontWeight={600}>
                    {title}
                </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
                {desc}
            </Typography>
            <Button
                variant="contained"
                onClick={onClick}
                disabled={disabled || loading}
                sx={{ alignSelf: "flex-start", mt: 1 }}
                size="small"
            >
                {btn}
            </Button>
        </Box>
    );
}

function OtpInputAdapter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
    return (
        <OtpInput
            value={value}
            onChange={onChange}
            numInputs={6}
            shouldAutoFocus
            renderInput={(props) => (
                <input
                    {...props}
                    style={{
                        width: 40,
                        height: 48,
                        margin: "0 4px",
                        fontSize: 20,
                        textAlign: "center",
                        borderRadius: 6,
                        border: "1px solid #ccc",
                        outline: "none",
                    }}
                    inputMode="numeric"
                />
            )}
        />
    );
}
